import { create } from 'zustand';
import { robotTxtService, RobotTxt, UpdateRobotTxtDto, RobotTxtValidationResult } from '@/services/robotTxtService';
import { showToast } from '@/lib/toast';

interface RobotTxtState {
    robotTxt: RobotTxt | null;
    isLoading: boolean;
    isUpdating: boolean;
    isResetting: boolean;
    isToggling: boolean;
    isValidating: boolean;
    error: string | null;
    validationResult: RobotTxtValidationResult | null;

    // Actions
    fetchRobotTxt: () => Promise<void>;
    updateRobotTxt: (data: UpdateRobotTxtDto) => Promise<boolean>;
    resetRobotTxt: () => Promise<boolean>;
    toggleStatus: () => Promise<boolean>;
    validateContent: (content: string) => Promise<void>;
    clearError: () => void;
    clearValidation: () => void;
}

export const useRobotTxtStore = create<RobotTxtState>()((set, _get) => ({
    robotTxt: null,
    isLoading: false,
    isUpdating: false,
    isResetting: false,
    isToggling: false,
    isValidating: false,
    error: null,
    validationResult: null,

    fetchRobotTxt: async () => {
        set({ isLoading: true, error: null });
        try {
            const response: any = await robotTxtService.get();
            console.log("response: ", response.data.data?.data)
            if (response.ok && response.data) {
                set({ robotTxt: response?.data?.data?.data, isLoading: false });
            } else {
                set({ error: 'Không thể tải nội dung robots.txt', isLoading: false });
                showToast.error("Lỗi", "Không thể tải nội dung robots.txt");
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi tải robots.txt';
            set({ error: errorMessage, isLoading: false });
            showToast.error("Lỗi", errorMessage);
        }
    },

    updateRobotTxt: async (data: UpdateRobotTxtDto) => {
        set({ isUpdating: true, error: null });
        try {
            const response = await robotTxtService.update(data);
            if (response.ok && response.data) {
                set({ robotTxt: response.data, isUpdating: false });
                showToast.success("Thành công", "Cập nhật robots.txt thành công");
                return true;
            } else {
                set({ error: 'Không thể cập nhật robots.txt', isUpdating: false });
                showToast.error("Lỗi", "Không thể cập nhật robots.txt");
                return false;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi cập nhật robots.txt';
            set({ error: errorMessage, isUpdating: false });
            showToast.error("Lỗi", errorMessage);
            return false;
        }
    },

    resetRobotTxt: async () => {
        set({ isResetting: true, error: null });
        try {
            const response = await robotTxtService.reset();
            if (response.ok && response.data) {
                set({ robotTxt: response.data, isResetting: false });
                showToast.success("Thành công", "Reset robots.txt thành công");
                return true;
            } else {
                set({ error: 'Không thể reset robots.txt', isResetting: false });
                showToast.error("Lỗi", "Không thể reset robots.txt");
                return false;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi reset robots.txt';
            set({ error: errorMessage, isResetting: false });
            showToast.error("Lỗi", errorMessage);
            return false;
        }
    },

    toggleStatus: async () => {
        set({ isToggling: true, error: null });
        try {
            const response = await robotTxtService.toggleStatus();
            if (response.ok && response.data) {
                set({ robotTxt: response.data, isToggling: false });
                const status = response.data.isActive ? 'kích hoạt' : 'vô hiệu hóa';
                showToast.success("Thành công", `Robots.txt đã được ${status}`);
                return true;
            } else {
                set({ error: 'Không thể thay đổi trạng thái', isToggling: false });
                showToast.error("Lỗi", "Không thể thay đổi trạng thái");
                return false;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi thay đổi trạng thái';
            set({ error: errorMessage, isToggling: false });
            showToast.error("Lỗi", errorMessage);
            return false;
        }
    },

    validateContent: async (content: string) => {
        set({ isValidating: true, error: null });
        try {
            const response = await robotTxtService.validate({ content });
            if (response.ok && response.data) {
                const result: any = (response as any).data?.data ?? (response as any).data;
                set({ validationResult: result, isValidating: false });
                if (result?.isValid) {
                    showToast.success("Thành công", "Nội dung robots.txt hợp lệ");
                } else {
                    showToast.warning("Cảnh báo", "Nội dung robots.txt có lỗi");
                }
            } else {
                set({ error: 'Không thể validate nội dung', isValidating: false });
                showToast.error("Lỗi", "Không thể validate nội dung");
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Lỗi khi validate nội dung';
            set({ error: errorMessage, isValidating: false });
            showToast.error("Lỗi", errorMessage);
        }
    },

    clearError: () => set({ error: null }),
    clearValidation: () => set({ validationResult: null }),
}));
